import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { INR, prettyDate } from "@/lib/format";
import { Plus, FilePen, Trash2, Link2, ArrowRight, TrendingUp, Wallet } from "lucide-react";
import Can from "@/components/Can";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const JTYPES = ["Sales Return", "Customer Discount", "Adjustment"];

function MoneyCard({ title, icon: Icon, accent, block, unit, testid }) {
  return (
    <div className={`bg-white border rounded-md p-5 flex flex-col gap-3 ${accent.border}`} data-testid={testid}>
      <div className={`text-[11px] uppercase tracking-wider font-bold flex items-center gap-2 ${accent.text}`}><Icon size={14}/> {title}</div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500">Today's {title}</div>
        <div className={`mono text-3xl sm:text-4xl font-bold leading-tight ${accent.text}`} data-testid={`${testid}-today`}>{INR(block?.today || 0)}</div>
        <div className="text-xs text-slate-500 mt-0.5" data-testid={`${testid}-count`}>{block?.today_count || 0} {unit}{(block?.today_count || 0) === 1 ? "" : "s"} today</div>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500">This Month's {title}</div>
        <div className="mono text-xl font-semibold" data-testid={`${testid}-month`}>{INR(block?.month || 0)}</div>
      </div>
      <div className="pt-3 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
        <span>Last Month's {title}</span><span className="mono" data-testid={`${testid}-last-month`}>{INR(block?.last_month || 0)}</span>
      </div>
    </div>
  );
}

function Panel({ title, children, testid, right }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md table-scroll" data-testid={testid}>
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-2"><div className="text-sm font-semibold">{title}</div>{right}</div>
      {children}
    </div>
  );
}

const Empty = ({ cols, text = "Nothing issued yet today" }) => <tr><td colSpan={cols} className="text-center py-8 text-slate-400 text-sm">{text}</td></tr>;

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [loc, setLoc] = useState(null);
  const load = () => api.get("/dashboard").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (d && loc === null) setLoc(""); }, [d]); // eslint-disable-line react-hooks/exhaustive-deps
  const dropDraft = async (x) => { if (!window.confirm(`Delete draft ${x.label} (${x.customer_name})?`)) return; await api.delete(`/drafts/${x.id}`); toast.success("Draft deleted"); load(); };

  const ledger = useMemo(() => {
    if (!d) return [];
    let bal = 0;
    return (d.daybook || []).filter(e => !loc || e.location === loc).map(e => { bal += e.in - e.out; return { ...e, balance: bal }; });
  }, [d, loc]);
  const totIn = ledger.reduce((s, e) => s + e.in, 0), totOut = ledger.reduce((s, e) => s + e.out, 0);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{fontFamily: 'Plus Jakarta Sans'}}>Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Today · <span className="mono">{d ? prettyDate(d.today) : ""}</span> · sales, collections and the day's ledger.</p>
        </div>
        <Can perm="vouchers"><Link to="/invoices/new" data-testid="new-invoice-btn" className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold uppercase tracking-wider px-3.5 py-2 rounded-md flex items-center gap-2"><Plus size={14}/> New Invoice</Link></Can>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <MoneyCard title="Sales" icon={TrendingUp} accent={{ text: "text-blue-800", border: "border-blue-200" }} block={d?.sales} unit="invoice" testid="card-sales"/>
        <MoneyCard title="Receipts" icon={Wallet} accent={{ text: "text-emerald-800", border: "border-emerald-200" }} block={d?.receipts} unit="receipt" testid="card-receipts"/>
      </div>

      {d?.drafts?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md table-scroll" data-testid="drafts-card">
          <div className="px-4 py-3 border-b border-amber-200 flex items-center justify-between">
            <div className="text-sm font-semibold flex items-center gap-2 text-amber-900"><FilePen size={15}/> Draft Invoices <span className="text-xs text-amber-700 font-normal mono">({d.drafts.length})</span></div>
            <div className="text-[11px] text-amber-800">Auto-saved while typing · invoice number is issued only on Save</div>
          </div>
          <table className="acct-table fit">
            <thead><tr><th>Draft #</th><th>Customer</th><th>Location</th><th>Date</th><th className="num">Amount</th><th className="text-right">Convert</th></tr></thead>
            <tbody>
              {d.drafts.map((x, i) => (
                <tr key={x.id} data-testid={`draft-row-${i}`}>
                  <td className="mono font-medium text-amber-900" data-testid={`draft-label-${i}`}>{x.label}</td>
                  <td className="font-medium">{x.customer_name}<span className="ml-2 text-[11px] text-slate-500">{x.lines} line{x.lines === 1 ? "" : "s"}</span></td>
                  <td className="text-xs text-slate-600">{x.location || "-"}</td>
                  <td className="mono">{prettyDate(x.date)}</td>
                  <td className="num mono font-medium">{INR(x.amount)}</td>
                  <td className="text-right whitespace-nowrap">
                    <Can perm="vouchers">
                      <Link to={`/invoices/new?draft=${x.id}&focus=save`} data-testid={`draft-convert-${i}`} className="inline-flex items-center gap-1 h-7 px-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded text-[11px] font-semibold uppercase tracking-wider">Convert to Invoice <ArrowRight size={12}/></Link>
                      <button data-testid={`draft-delete-${i}`} onClick={() => dropDraft(x)} className="p-1.5 ml-1 text-slate-400 hover:text-rose-700" title="Delete draft"><Trash2 size={13}/></button>
                    </Can>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <Panel title="Today's Invoices Issued" testid="today-invoices" right={<span className="text-xs text-slate-500 mono">{d?.today_invoices?.length || 0}</span>}>
          <table className="acct-table fit">
            <thead><tr><th>Invoice</th><th>Customer</th><th>Location</th><th>Date</th><th className="num">Amount</th></tr></thead>
            <tbody>
              {(d?.today_invoices || []).map((i, n) => (
                <tr key={i.id} data-testid={`today-invoice-${n}`}>
                  <td><Link to={`/invoices/${i.id}`} className="mono font-medium text-blue-700 hover:underline">{i.invoice_number}</Link></td>
                  <td><Link to={`/customers/${i.customer_id}`} className="hover:underline">{i.customer_name}</Link></td>
                  <td className="text-xs text-slate-600">{i.location || "-"}</td>
                  <td className="mono">{prettyDate(i.date)}</td>
                  <td className="num mono font-medium">{INR(i.total)}</td>
                </tr>
              ))}
              {d && !d.today_invoices.length && <Empty cols={5}/>}
            </tbody>
          </table>
        </Panel>
        <Panel title="Today's Receipts" testid="today-receipts" right={<span className="text-xs text-slate-500 mono">{d?.today_receipts?.length || 0}</span>}>
          <table className="acct-table fit">
            <thead><tr><th>Receipt</th><th>Date</th><th>Customer</th><th>Mode</th><th className="num">Amount</th></tr></thead>
            <tbody>
              {(d?.today_receipts || []).map((r, n) => (
                <tr key={r.id} data-testid={`today-receipt-${n}`}>
                  <td className="mono font-medium whitespace-nowrap">{r.receipt_number}{r.linked_invoice_id && <Link to={`/invoices/${r.linked_invoice_id}`} title="Linked to invoice" className="inline-flex ml-1 text-blue-700 align-middle"><Link2 size={11}/></Link>}</td>
                  <td className="mono">{prettyDate(r.date)}</td>
                  <td><Link to={`/customers/${r.customer_id}`} className="hover:underline">{r.customer_name}</Link></td>
                  <td className="text-xs">{r.mode}</td>
                  <td className="num mono font-medium text-emerald-800">{INR(r.amount)}</td>
                </tr>
              ))}
              {d && !d.today_receipts.length && <Empty cols={5} text="No receipts yet today"/>}
            </tbody>
          </table>
        </Panel>
      </div>

      <div className="bg-white border border-slate-200 rounded-md table-scroll" data-testid="today-daybook">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-semibold mr-2">Today's Day Book</span>
            {["", ...(d?.locations || [])].map(l => (
              <button key={l || "__all"} data-testid={`daybook-tab-${l ? l.replace(/\s+/g, "-").toLowerCase() : "all"}`} onClick={() => setLoc(l)}
                className={`h-7 px-2.5 text-[11px] font-semibold uppercase tracking-wider rounded ${(loc || "") === l ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"}`}>{l || "All locations"}</button>
            ))}
          </div>
          <Link to="/reports" data-testid="daybook-full-link" className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1">View full Day Book <ArrowRight size={12}/></Link>
        </div>
        <table className="acct-table" data-testid="daybook-table">
          <thead><tr><th>Reference</th><th>Type</th><th>Particulars</th><th>Mode</th><th className="num">In</th><th className="num">Out</th><th className="num">Balance</th></tr></thead>
          <tbody>
            {ledger.map((e, n) => (
              <tr key={`${e.type}-${e.ref}-${n}`} data-testid={`daybook-entry-${n}`}>
                <td className="mono font-medium">{e.ref_id ? <Link to={`/invoices/${e.ref_id}`} className="text-blue-700 hover:underline">{e.ref}</Link> : e.ref}</td>
                <td><span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${e.type === "Invoice" ? "bg-blue-50 text-blue-800 border-blue-200" : e.type === "Receipt" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-700 border-slate-200"}`}>{e.type}</span></td>
                <td className="text-xs text-slate-600 max-w-md truncate" title={e.particulars}>{e.particulars}{!loc && e.location && <span className="text-slate-400"> · {e.location}</span>}</td>
                <td className="text-xs">{e.mode || "—"}</td>
                <td className="num mono text-emerald-800">{e.in ? INR(e.in) : "-"}</td>
                <td className="num mono">{e.out ? INR(e.out) : "-"}</td>
                <td className={`num mono font-medium ${e.balance < 0 ? "text-rose-800" : ""}`}>{INR(e.balance)}</td>
              </tr>
            ))}
            {d && !ledger.length && <Empty cols={7} text={loc ? `No entries today for ${loc}` : "No transactions yet today"}/>}
            {ledger.length > 0 && <tr className="bg-slate-100 font-bold" data-testid="daybook-totals"><td colSpan="4">Day total{loc ? ` · ${loc}` : ""}{!loc && JTYPES.some(t => ledger.some(e => e.type === t)) ? " (journals included)" : ""}</td><td className="num mono text-emerald-800">{INR(totIn)}</td><td className="num mono">{INR(totOut)}</td><td className={`num mono ${totIn - totOut < 0 ? "text-rose-800" : ""}`}>{INR(totIn - totOut)}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
